import { Currency } from "@redotech/money/currencies";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnItemParams } from "@redotech/redo-customer-portal-shared/util";
import { useTranslation } from "@redotech/redo-i18n";
import { UpsellBillingTrigger } from "@redotech/redo-model/return";
import { ReturnTotals } from "@redotech/redo-model/return-totals-calculator";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { capitalizeWords } from "@redotech/string/util";
import { useContext } from "react";
import { CustomizationContext } from "../../../customization/customization-context";
import { SummaryCard } from "../../submission/components/summary-card";

export function SummaryCardConfirmation({
  totals,
  currency,
  feesHeader,
  feesOrderHeader,
  products,
  newItems,
  labelDeducted,
  settings,
  upsellBillingTrigger,
  isConvenienceFee,
}: {
  feesHeader: string;
  feesOrderHeader: string;
  totals: ReturnTotals;
  currency: Currency;
  products: ReturnItemParams[] | undefined;
  newItems: ReturnItemParams[] | undefined;
  labelDeducted: boolean;
  settings: CustomerPortalAppSettings | undefined;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  isConvenienceFee: boolean;
}) {
  const { t } = useTranslation();
  const { customization } = useContext(CustomizationContext);
  const adjustmentText = customization?.pages?.review?.text;
  const { isLoading, productSubtotals } = getConfirmationSummary({
    totals,
    products,
  });

  const {
    isLoading: isLoadingNewItems,
    productSubtotals: newOrderItemsSubtotals,
  } = getConfirmationSummary({ totals, products: newItems });

  if (isLoading || isLoadingNewItems) {
    return <LoadingRedoAnimation />;
  }
  const bonusText =
    adjustmentText?.adjustmentPositiveName ||
    settings?.returnAdjustment?.positiveName;
  const feeText =
    adjustmentText?.adjustmentNegativeName ||
    settings?.returnAdjustment?.negativeName;
  const adjustmentName = totals.totalAdjustmentValue > 0 ? bonusText : feeText;
  return (
    <SummaryCard
      adjustmentValue={totals.totalAdjustmentValue}
      adjustmentValueString={capitalizeWords(adjustmentName ?? "")}
      currency={currency}
      exchangeShippingFee={totals.exchangeShippingFee}
      exchangeShippingName={totals.exchangeShippingName}
      feesHeader={feesHeader}
      feesOrderHeader={feesOrderHeader}
      isConvenienceFee={isConvenienceFee}
      labelDeducted={labelDeducted}
      newOrderItemsSubtotals={newOrderItemsSubtotals}
      newOrderTaxes={[
        { title: t("Taxes"), total: totals.advancedExchangeItemsTaxInNewOrder },
      ]}
      newOrderValue={totals.newOrderValue}
      newVariantExchangeItemSubtotals={[]}
      pickupFee={undefined}
      pointsDeductedFromRefundSubtotal={
        totals.totalReturnValueDeductedDueToLoyaltyPoints
          ? {
              title: t("Loyalty Points Deducted"),
              total: -totals.totalReturnValueDeductedDueToLoyaltyPoints,
            }
          : undefined
      }
      productSubtotals={productSubtotals}
      returnValue={totals.totalReturnValue - totals.totalAdjustmentValue}
      shippingFee={totals.shippingFee}
      shippingFeeDeducted={totals.shippingFeeDeducted}
      submitted
      taxSubtotals={[
        { title: t("Taxes"), total: totals.totalTaxes },
        ...(totals.totalDuty
          ? [{ title: t("Duties"), total: totals.totalDuty }]
          : []),
      ]}
      totalLoyaltyPointsDeducted={totals.totalLoyaltyPointsDeducted}
      totalLoyaltyPointsRefunded={totals.totalLoyaltyPointsRefunded}
      totalPaymentOwed={totals.totalPaymentOwed}
      totalRefund={totals.totalRefundWithoutShipping}
      totalShippingFee={totals.shippingFee + totals.exchangeShippingFee}
      totalStoreCredit={totals.totalStoreCredit}
      upsellBillingTrigger={upsellBillingTrigger}
    />
  );
}

interface GetConfirmationSummaryParams {
  totals: ReturnTotals;
  products: ReturnItemParams[] | undefined;
}

interface GetConfirmationSummaryResponse {
  isLoading: boolean;
  productSubtotals: { title: string; total: number }[];
}

function getConfirmationSummary({
  totals,
  products,
}: GetConfirmationSummaryParams): GetConfirmationSummaryResponse {
  function getTitle(product: ReturnItemParams | undefined): string {
    if (product?.subtitle == "Default Title") {
      return product?.title;
    }
    return product?.title + " - " + product?.subtitle;
  }

  const productSubtotals =
    products?.map((product) => {
      return {
        title: getTitle(product),
        total:
          +(product.itemValue ?? product.price) *
          product.quantity *
          (product.exchangeRate ?? 1),
      };
    }) ?? [];

  return { isLoading: !products, productSubtotals };
}
